import React, { useEffect } from "react";
import { Api } from "../../api";
import {
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  makeStyles,
  createStyles,
  Theme,
  Checkbox,
  CircularProgress,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/SearchOutlined";
import ClearIcon from "@material-ui/icons/Clear";
import Employee from "../../types/Employee";
import UserAvatar from "../UserAvatar";
import { hasError } from "../../helpers/ResponseHelper";
import useDebounce from "../../hooks/UseDebounce";

interface IProps {
  companyId: string;
  selectedIds: string[];
  employeesArray: Array<Employee>;
  onItemSelected: (item: Employee) => void;
}

export default function EmployeePicker(props: IProps) {
  const { companyId, selectedIds, employeesArray, onItemSelected } = props;
  const [data, setData] = React.useState(Array<Employee>());
  const [searchText, setSearchText] = React.useState("");
  const classes = useStyles();
  const debouncedSearchText = useDebounce(searchText, 500);
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (debouncedSearchText) {
      if (debouncedSearchText.length > 1) {
        setLoading(true);
        Api.employees
          .searchByCompany(companyId, debouncedSearchText)
          .then((res) => {
            if (!hasError(res)) {
              setData(res.data?.data ?? []);
            }
          })
          .finally(() => setLoading(false));
      }
    } else {
      setData(employeesArray ?? []);
    }
  }, [debouncedSearchText, employeesArray, companyId]);

  return (
    <>
      <TextField
        fullWidth
        variant="outlined"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                disabled={!searchText}
                onClick={() => setSearchText("")}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        className="tableSearch"
      />
      <div className="picker-content">
        <List className={(classes.listRoot, "picker")}>
          {loading ? (
            <CircularProgress
              size={20}
              color="primary"
              className="spinner"
              style={{ marginTop: 50 }}
            />
          ) : (
              <>
                {data?.length > 0 ? (
                  data.map((data) => (
                    <ListItem key={data.employeeId}>
                      <Checkbox
                        edge="start"
                        checked={selectedIds.indexOf(data.employeeId) !== -1}
                        tabIndex={-1}
                        disableRipple
                        onClick={() => onItemSelected(data)}
                      />
                      <UserAvatar
                        type="table"
                        firstName={data.firstName}
                        lastName={data.lastName}
                      />
                      <div
                        className={classes.textContainer}
                        onClick={() => onItemSelected(data)}
                      >
                        <div className={classes.title}>
                          {data.firstName} {data.lastName}
                        </div>
                      </div>
                    </ListItem>
                  ))
                ) : (
                    <p style={{ textAlign: "center", marginTop: 50 }}></p>
                  )}
              </>
            )}
        </List>
      </div>
    </>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listRoot: {
      width: "100%",
      backgroundColor: theme.palette.background.paper,
      position: "relative",
      overflow: "auto",
      maxHeight: 300,
    },
    title: {
      textTransform: "uppercase",
      fontSize: 16,
      color: "#222223",
    },
    desc: {
      fontSize: 11,
      color: "#222223",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "350px",
    },
    textContainer: {
      cursor: "pointer",
      flex: 1,
    },
  })
);
